/**
 * The three sign-ins, and how to leave all of them.
 *
 * This application has THREE separate audiences with three separate account
 * tables, three login endpoints and three tokens:
 *
 *   staff    POST /auth/login        `users`                       JWT sub = 12
 *   patient  POST /api/portal/login  portal_account/_identity      JWT sub = "portal:34"
 *   rep      POST /api/rep/login     rep_account/rep_identity      JWT sub = "rep:56"
 *
 * The subjects are deliberately different SHAPES, not just different values:
 * a bare integer is a staff users.id and the other two are prefixed strings,
 * so a portal token can never be mistaken for a staff one by a route that
 * does `int(sub)`.
 *
 * They are also stored separately — staff in localStorage (it survives a
 * browser restart), portal and rep in sessionStorage (they do not) — which
 * means a person can genuinely be holding more than one at a time. That is
 * intended: a laboratory admin who is also a patient somewhere is two
 * accounts, not one.
 *
 * It is also why "log out" was ambiguous. Signing out of the staff session
 * left a portal token in place and vice versa, so the next visit looked like
 * a login that would not clear. `clearAllSessions` is the unambiguous answer:
 * it ends every one of them.
 */

export const STAFF_TOKEN_KEY = 'token';
export const STAFF_REFRESH_KEY = 'refresh_token';
export const PORTAL_TOKEN_KEY = 'portal_token';
export const REP_TOKEN_KEY = 'rep_token';

/** Which sign-ins this browser is currently holding. */
export function activeSessions() {
  const out = [];
  try {
    if (localStorage.getItem(STAFF_TOKEN_KEY)) out.push('staff');
    if (sessionStorage.getItem(PORTAL_TOKEN_KEY)) out.push('patient');
    if (sessionStorage.getItem(REP_TOKEN_KEY)) out.push('rep');
  } catch { /* storage can be blocked; treat as none */ }
  return out;
}

/**
 * End every session this browser holds.
 *
 * Storage is cleared FIRST and unconditionally. The server-side revoke is
 * best-effort and deliberately cannot block it: a failing network call must
 * never be the reason somebody is stuck signed in with no way out.
 */
export async function clearAllSessions({ revoke = true } = {}) {
  let refresh = null;
  try {
    refresh = localStorage.getItem(STAFF_REFRESH_KEY);
    localStorage.removeItem(STAFF_TOKEN_KEY);
    localStorage.removeItem(STAFF_REFRESH_KEY);
    sessionStorage.removeItem(PORTAL_TOKEN_KEY);
    sessionStorage.removeItem(REP_TOKEN_KEY);
  } catch { /* nothing more we can do */ }

  if (revoke && refresh) {
    try {
      const api = (await import('./api')).default;
      await api.post('/auth/logout', { refresh_token: refresh });
    } catch { /* already signed out locally, which is what matters */ }
  }
}
